import { allows } from "@giftforward/entitlements";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useId } from "react";
import Accordion from "@/components/campaignBuilder/Accordion";
import { SCHEDULE_MODE_LABELS } from "@/constants/campaignBuilder";
import { LAUNCH_BLOCKER_TEXT } from "@/constants/review";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { downloadRecipientTemplate } from "@/lib/recipientTemplate";
import {
  isScheduleDateMissing,
  isScheduleTooSoon,
  scheduleMinDate,
  scheduleMinTime,
  scheduleTimeAfterDatePick,
} from "@/utils/scheduleDate-helper";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";
import { ConditionalPanel, RadioOption, StyledInput, StyledSelect } from "./shared";

interface Props {
  isOpen: boolean;
  onToggle: () => void;
}

export default function Schedule({ isOpen, onToggle }: Props) {
  const {
    scheduleMode,
    setScheduleMode,
    scheduleDate,
    setScheduleDate,
    scheduleTime,
    setScheduleTime,
    scheduleTz,
    setScheduleTz,
    openModal,
    openPlanModal,
  } = useCampaignBuilder();
  const { ent } = useMyEntitlements();
  const canSchedulePerRecipient =
    !!ent && allows(ent, "campaign_send", "scheduling", "per_recipient");

  const handleSelectDifferentDates = () => {
    if (!canSchedulePerRecipient) {
      openModal({
        context: "schedulePerRecipient",
        onPrimary: () => openPlanModal("schedulePerRecipient"),
      });
    } else {
      setScheduleMode("different");
    }
  };

  // GFG-293: a cleared date input leaves "certain" with no date. Same sentence Step 4's CTA shows,
  // so the two steps never disagree about what is wrong.
  const dateMissing = isScheduleDateMissing(scheduleMode, scheduleDate);
  const dateErrorId = useId();
  // GFG-432: today is pickable; the earliest time is an hour out. Everything is read in the campaign's zone.
  const zone = resolveScheduleTimeZone(scheduleTz);
  const timeTooSoon =
    scheduleMode === "certain" &&
    !dateMissing &&
    isScheduleTooSoon(scheduleDate, scheduleTime, zone);
  const timeErrorId = useId();

  const description =
    scheduleMode === "asap"
      ? SCHEDULE_MODE_LABELS.asap
      : scheduleMode === "certain"
        ? dateMissing
          ? LAUNCH_BLOCKER_TEXT.schedule_missing
          : `Scheduled for ${scheduleDate}`
        : "Different dates per recipient";

  return (
    <Accordion
      title="Schedule"
      description={description}
      icon={<ScheduleIcon sx={{ fontSize: 24 }} />}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-2 mt-4">
        <RadioOption
          selected={scheduleMode === "asap"}
          onSelect={() => setScheduleMode("asap")}
          label={SCHEDULE_MODE_LABELS.asap}
          hint="Deliver gifts immediately when the campaign launches."
        />
        <RadioOption
          selected={scheduleMode === "certain"}
          onSelect={() => setScheduleMode("certain")}
          label={SCHEDULE_MODE_LABELS.certain}
          hint="All recipients receive their gift at the same date and time."
          hasVisiblePanel={scheduleMode === "certain"}
        />
        <ConditionalPanel visible={scheduleMode === "certain"} merged>
          <p className="font-lato text-[12px] font-bold uppercase tracking-[1px] text-graphite mb-2">
            Send gifts on:
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <StyledInput
              type="date"
              value={scheduleDate}
              min={scheduleMinDate(zone)}
              onChange={(e) => {
                const nextDate = e.target.value;
                setScheduleDate(nextDate);
                setScheduleTime(scheduleTimeAfterDatePick(nextDate, scheduleTime, zone));
              }}
              aria-invalid={dateMissing}
              aria-describedby={dateMissing ? dateErrorId : undefined}
              style={dateMissing ? { border: "1px solid var(--color-good-wine)" } : undefined}
            />
            <StyledInput
              type="time"
              value={scheduleTime}
              min={scheduleMinTime(scheduleDate, zone)}
              onChange={(e) => setScheduleTime(e.target.value)}
              aria-invalid={timeTooSoon}
              aria-describedby={timeTooSoon ? timeErrorId : undefined}
              style={timeTooSoon ? { border: "1px solid var(--color-good-wine)" } : undefined}
            />
            <StyledSelect value={scheduleTz} onChange={(e) => setScheduleTz(e.target.value)}>
              <option value="PT">Pacific (PT)</option>
              <option value="MT">Mountain (MT)</option>
              <option value="CT">Central (CT)</option>
              <option value="ET">Eastern (ET)</option>
            </StyledSelect>
          </div>
          {dateMissing && (
            <p
              id={dateErrorId}
              role="alert"
              className="mt-2 mb-0 font-lato text-[12.5px] font-semibold text-good-wine"
            >
              {LAUNCH_BLOCKER_TEXT.schedule_missing}
            </p>
          )}
          {timeTooSoon && (
            <p
              id={timeErrorId}
              role="alert"
              className="mt-2 mb-0 font-lato text-[12.5px] font-semibold text-good-wine"
            >
              Pick a time at least 1 hour from now.
            </p>
          )}
        </ConditionalPanel>
        <RadioOption
          selected={scheduleMode === "different"}
          onSelect={handleSelectDifferentDates}
          label={SCHEDULE_MODE_LABELS.different}
          hint="Ideal for birthdays, work anniversaries, or staggered sends. You'll upload a schedule in the Recipients step."
          pro={!canSchedulePerRecipient}
          hasVisiblePanel={scheduleMode === "different"}
        />
        <ConditionalPanel visible={scheduleMode === "different"} merged>
          <div className="flex items-center gap-2.5">
            <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: "var(--color-good-ochre)" }} />
            <div className="flex-1 min-w-0">
              <p className="font-lato text-[13px] font-semibold text-rodeo-black">
                Download Schedule Template
              </p>
              <p className="font-lato text-[12px] text-graphite">
                You'll be asked for individual send dates for each contact in the Recipients step.
              </p>
            </div>
            <button
              type="button"
              onClick={() => downloadRecipientTemplate(ent)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-btn font-lato text-[12px] font-semibold text-graphite transition-all shrink-0 border border-smoke bg-white hover:border-dark-dust hover:text-dark-dust"
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
              CSV Template
            </button>
          </div>
        </ConditionalPanel>
      </div>
    </Accordion>
  );
}
